import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import DialPad from './DialPad';
import { useLanguage } from '../LanguageContext';
import translations from '../translations';

function CheckOut({ orgId }) {
  const { language } = useLanguage();
  const t = translations[language];
  const [step, setStep] = useState(1);
  const [searchPhone, setSearchPhone] = useState('');
  const [checkedInKids, setCheckedInKids] = useState([]);
  const [selectedKids, setSelectedKids] = useState([]);
  const [enteredPin, setEnteredPin] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSearch = async () => {
    setMessage('');
    setError('');

    if (!searchPhone) {
      setError(t.pleaseEnterPhoneSearch);
      return;
    }

    const { data: children, error: childError } = await supabase
      .from('children')
      .select('*')
      .ilike('parent_phone', `%${searchPhone}%`);

    if (childError || !children.length) {
      setError(t.noChildrenFound);
      setCheckedInKids([]);
      return;
    }

    const childIds = children.map((c) => c.id);
    const { data: checkins, error: checkinError } = await supabase
      .from('checkins')
      .select('*')
      .in('child_id', childIds)
      .eq('status', 'checked_in');

    if (checkinError || !checkins.length) {
      setError(t.noActiveCheckins);
      setCheckedInKids([]);
      return;
    }

    const results = checkins.map((checkin) => {
      const child = children.find((c) => c.id === checkin.child_id);
      return { ...child, checkin_id: checkin.id, checked_in_at: checkin.checked_in_at };
    });

    setCheckedInKids(results);
    setStep(2);
  };

  const toggleKidSelection = (kid) => {
    setSelectedKids((prev) =>
      prev.find((k) => k.checkin_id === kid.checkin_id)
        ? prev.filter((k) => k.checkin_id !== kid.checkin_id)
        : [...prev, kid]
    );
  };

  const handleProceedToPin = () => {
    if (selectedKids.length === 0) {
      setError(t.pleaseSelectChild);
      return;
    }
    setEnteredPin('');
    setError('');
    setStep(3);
  };

  const handleVerifyPin = async () => {
    setError('');

    if (!enteredPin || enteredPin.length !== 4) {
      setError(t.pleaseEnter4Pin);
      return;
    }

    const pinToCheck = checkedInKids[0].pickup_pin;

    if (enteredPin !== pinToCheck) {
      setError(t.incorrectPin);
      return;
    }

    for (const kid of selectedKids) {
      const { error } = await supabase
        .from('checkins')
        .update({ status: 'parent_waiting', parent_waiting_at: new Date() })
        .eq('id', kid.checkin_id);

      if (error) {
        setError('Error: ' + error.message);
        return;
      }
    }

    const names = selectedKids.map((k) => k.full_name).join(', ');
    setMessage(t.waitingMessage(names, selectedKids.length > 1));
    setStep(4);
  };

  return (
    <div>
      <div className="header">
        <h2>{t.pickUpChild}</h2>
        <p>{t.enterPhoneToStart}</p>
      </div>

      {/* Step 1 - Phone Search */}
      {step === 1 && (
        <div>
          <DialPad value={searchPhone} onChange={setSearchPhone} maxLength={10} />
          <button className="btn btn-primary" onClick={handleSearch}>{t.search}</button>
          {error && <div className="error-message">{error}</div>}
        </div>
      )}

      {/* Step 2 - Select Children */}
      {step === 2 && (
        <div>
          <p className="section-title">{t.selectChildrenPickup}</p>

          {checkedInKids.map((kid) => {
            const selected = !!selectedKids.find((k) => k.checkin_id === kid.checkin_id);
            return (
              <div
                key={kid.checkin_id}
                className="kid-card"
                style={{
                  cursor: 'pointer',
                  border: selected ? '2px solid #1a56db' : undefined,
                  background: selected ? '#f0f4ff' : undefined,
                }}
                onClick={() => toggleKidSelection(kid)}
              >
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%',
                  border: '2px solid #1a56db', background: selected ? '#1a56db' : '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, color: '#fff', fontSize: '16px'
                }}>
                  {selected ? '✓' : ''}
                </div>
                <div className="kid-info">
                  <h3>👶 {kid.full_name}</h3>
                  <p>{t.checkedInAt}: {new Date(kid.checked_in_at).toLocaleTimeString()}</p>
                </div>
              </div>
            );
          })}

          <button
            className="btn btn-primary"
            style={{ marginTop: '8px' }}
            onClick={handleProceedToPin}
            disabled={selectedKids.length === 0}
          >
            {t.continueWith(selectedKids.length)}
          </button>

          <button className="btn btn-gray" style={{ width: '100%', marginTop: '10px' }} onClick={() => setStep(1)}>
            {t.back}
          </button>
          {error && <div className="error-message">{error}</div>}
        </div>
      )}

      {/* Step 3 - PIN Verification */}
      {step === 3 && (
        <div>
          <div className="header">
            <h2>{t.enterPickupPin}</h2>
            <p>{t.verifyingPickupFor(selectedKids.map((k) => k.full_name).join(', '))}</p>
          </div>

          <DialPad value={enteredPin} onChange={setEnteredPin} maxLength={4} />

          <div style={disclaimerStyle}>
            <p style={{ margin: 0, fontSize: '13px', color: '#856404' }}>
              {t.forgotPin} {t.forgotPinDesc}
            </p>
          </div>

          <button className="btn btn-primary" onClick={handleVerifyPin}>
            {t.verifyNotifyStaff}
          </button>
          <button className="btn btn-gray" style={{ width: '100%', marginTop: '10px' }} onClick={() => setStep(2)}>
            {t.back}
          </button>
          {error && <div className="error-message">{error}</div>}
        </div>
      )}

      {/* Step 4 - Success */}
      {step === 4 && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ fontSize: '60px', marginBottom: '20px' }}>⏳</div>
          <h2 style={{ marginBottom: '12px' }}>{t.staffNotified}</h2>
          <div className="success-message">{message}</div>
          <p style={{ color: '#888', marginTop: '20px', fontSize: '14px' }}>
            {t.remainInArea}
          </p>
        </div>
      )}
    </div>
  );
}

const disclaimerStyle = {
  backgroundColor: '#fff3cd',
  border: '1px solid #ffc107',
  borderRadius: '10px',
  padding: '12px 16px',
  marginBottom: '16px',
  marginTop: '4px',
};

export default CheckOut;